import React from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'

import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'

export type ActivityView = 'transactions' | 'dca'

type Props = {
  value: ActivityView
  onChange: (value: ActivityView) => void
  /** Phone-sized pill (mobile-web phone layout, extension popup). */
  compact?: boolean
}

/**
 * Transactions | DCA segmented pill (design). Same pill as Swap | DCA on the
 * swap card. Shared by the Activity screen (mobile-web, extension Settings) and
 * the extension dashboard's Activity tab.
 */
const ActivityViewToggle = ({ value, onChange, compact }: Props) => {
  const { theme } = useTheme()
  const { t } = useTranslation()

  const viewTabs: { value: ActivityView; label: string }[] = [
    { value: 'transactions', label: t('Transactions') },
    { value: 'dca', label: t('DCA') }
  ]

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        height: compact ? 34 : 40,
        padding: 3,
        borderRadius: RADII.pill,
        borderWidth: 1,
        borderColor: theme.homeCardBorder as string,
        backgroundColor: theme.homeCardBackground as string
      }}
      accessibilityRole="tablist"
      testID="activity-view-toggle"
    >
      {viewTabs.map(({ value: tab, label }) => {
        const isActive = value === tab
        return (
          <Pressable
            key={tab}
            onPress={() => onChange(tab)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            testID={`activity-view-${tab}`}
            style={({ hovered }: any) => ({
              height: '100%',
              justifyContent: 'center',
              paddingHorizontal: compact ? 14 : 24,
              minWidth: compact ? 64 : 96,
              alignItems: 'center',
              borderRadius: RADII.pill,
              backgroundColor: isActive
                ? (theme.primaryBackgroundInverted as string)
                : 'transparent',
              opacity: !isActive && hovered ? 0.8 : 1
            })}
          >
            <Text
              fontSize={compact ? 13 : 14}
              weight="semiBold"
              numberOfLines={1}
              color={(isActive ? theme.primaryBackground : theme.secondaryText) as string}
            >
              {label}
            </Text>
          </Pressable>
        )
      })}
    </View>
  )
}

export default React.memo(ActivityViewToggle)
